import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { environment } from '../../environments/environment';
import { KeycloakService } from '../security/keycloak.service';
import { AlertNotification, AlertNotificationEvent } from '../models/alert-notification.model';

@Injectable({
  providedIn: 'root'
})
export class AlertNotificationService {
  private http = inject(HttpClient);
  private zone = inject(NgZone);
  private keycloakService = inject(KeycloakService);

  private readonly apiUrl = `${environment.apiBaseUrl}/notifications`;

  getAll(): Observable<AlertNotification[]> {
    return this.http.get<AlertNotification[]>(this.apiUrl);
  }

  getUnreadCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.apiUrl}/unread-count`);
  }

  markAsRead(id: number): Observable<AlertNotification> {
    return this.http.patch<AlertNotification>(`${this.apiUrl}/${id}/read`, {});
  }

  markAllAsRead(): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/read-all`, {});
  }

  listenForNotifications(): Observable<AlertNotificationEvent> {
    return new Observable((observer) => {
      let destroyed = false;
      let currentAbort: AbortController | null = null;

      const connect = async () => {
        if (destroyed) return;

        currentAbort = new AbortController();

        try {
          const token = await this.keycloakService.updateToken();
          if (destroyed) return;

          await fetchEventSource(`${this.apiUrl}/stream`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
            signal: currentAbort.signal,

            // Without this the library aborts the stream whenever the tab is hidden and reopens it
            // on return — and nothing replays what was pushed in between. A notification raised
            // while the operator was looking at another tab would simply never arrive, which is
            // the opposite of what a notification is for.
            openWhenHidden: true,

            onmessage: (event) => {
              if (event.event === 'notification') {
                this.zone.run(() => observer.next(JSON.parse(event.data)));
              }
            },

            onerror: (error) => {
              throw error;
            },
          });

          // Reached when the server ended the stream cleanly — the emitter's own timeout does this
          // every 30 minutes, and the library only retries from its error path. Reported as an
          // error rather than reconnected here, so the layout's backoff stays the one place that
          // decides when to come back.
          if (destroyed) return;
          this.zone.run(() => observer.error(new Error('SSE stream closed by the server')));
        } catch {
          if (destroyed) return;
          this.zone.run(() => observer.error(new Error('SSE connection failed')));
        }
      };

      connect();

      return () => {
        destroyed = true;
        currentAbort?.abort();
      };
    });
  }
}
